import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Alert,
    Snackbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel,
    Chip,
    Stack,
    Divider,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import {
    getAllTeams,
    getTeam,
    createTeam,
    updateTeam,
    deleteTeam,
    addMemberToTeam,
    removeMemberFromTeam,
    setTeamLead,
} from '../../services/teamService';
import { getAllProjectsNoPagination as getAllProjects } from '../../services/projectService';
import { getAllUsersNoPagination as getAllUsers } from '../../services/userService';

const Teams = () => {
    const [teams, setTeams] = useState([]);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        direction: 'asc',
        field: 'name'
    });
    const [availableUsers, setAvailableUsers] = useState([]);
    const [availableProjects, setAvailableProjects] = useState([]);
    const [activeProjects, setActiveProjects] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedProject, setSelectedProject] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        members: [],
        teamLead: null,
        projects: [],
    });

    useEffect(() => {
        fetchTeams();
        fetchProjects();
        fetchUsers();
    }, []);

    const fetchTeams = async () => {
        try {
            const data = await getAllTeams();
            setTeams(data);
            setError(null);
        } catch (error) {
            console.error('Error fetching teams:', error);
            setError(error.message);
            if (error.message.includes('authentication') || error.message.includes('token')) {
                window.location.href = '/login';
            }
        }
    };

    const fetchProjects = async () => {
        try {
            const data = await getAllProjects();
            // Keep all projects for displaying existing assignments
            const sortedProjects = data.sort((a, b) => a.name.localeCompare(b.name));
            setAvailableProjects(sortedProjects);
            
            // Filter active projects for new assignments
            const activeOnly = data.filter(project => project.active);
            const sortedActiveProjects = activeOnly.sort((a, b) => a.name.localeCompare(b.name));
            setActiveProjects(sortedActiveProjects);
        } catch (error) {
            console.error('Error fetching projects:', error);
            setError(error.message);
        }
    };

    const fetchUsers = async () => {
        try {
            const data = await getAllUsers();
            const activeUsers = data.filter(user => user.isActive);
            const sortedUsers = activeUsers.sort((a, b) => {
                const nameA = `${a.firstName} ${a.lastName}`;
                const nameB = `${b.firstName} ${b.lastName}`;
                return nameA.localeCompare(nameB);
            });
            setAvailableUsers(sortedUsers);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError(error.message);
        }
    };

    const handleFormOpen = (team = null) => {
        if (team) {
            // Check if current team lead still has team_lead role
            const teamLeadId = team.teamLead?._id;
            const teamLeadStillValid = teamLeadId ? 
                availableUsers.find(u => u._id === teamLeadId && u.role === 'team_lead') : false;

            setFormData({
                name: team.name,
                members: team.members?.map(m => m._id) || [],
                teamLead: teamLeadStillValid ? teamLeadId : null,
                projects: team.projects?.map(p => p._id) || [],
            });
            setSelectedTeam(team);
        } else {
            setFormData({
                name: '',
                members: [],
                teamLead: null,
                projects: [],
            });
            setSelectedTeam(null);
        }
        setIsFormOpen(true);
    };

    const handleFormClose = () => {
        setIsFormOpen(false);
        setSelectedTeam(null);
        setFormData({
            name: '',
            members: [],
            teamLead: null,
            projects: [],
        });
        setError(null);
        setSelectedUser('');
        setSelectedProject('');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAddMember = async () => {
        if (!selectedUser) return;
        try {
            if (selectedTeam) {
                await addMemberToTeam(selectedTeam._id, selectedUser);
                const updatedTeam = await getTeam(selectedTeam._id);
                setSelectedTeam(updatedTeam);
                setFormData(prev => ({
                    ...prev,
                    members: updatedTeam.members.map(m => m._id),
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    members: [...prev.members, selectedUser],
                }));
            }
            setSelectedUser('');
        } catch (error) {
            console.error('Error adding team member:', error);
            setError(error.message);
        }
    };

    const handleRemoveMember = async (userId) => {
        try {
            if (selectedTeam) {
                await removeMemberFromTeam(selectedTeam._id, userId);
                const updatedTeam = await getTeam(selectedTeam._id);
                setSelectedTeam(updatedTeam);
                setFormData(prev => ({
                    ...prev,
                    members: updatedTeam.members.map(m => m._id),
                    teamLead: updatedTeam.teamLead?._id || null,
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    members: prev.members.filter(id => id !== userId),
                    teamLead: prev.teamLead === userId ? null : prev.teamLead,
                }));
            }
        } catch (error) {
            console.error('Error removing team member:', error);
            setError(error.message);
        }
    };

    const handleTeamLeadChange = async (userId) => {
        try {
            if (selectedTeam) {
                await setTeamLead(selectedTeam._id, userId);
                const updatedTeam = await getTeam(selectedTeam._id);
                setSelectedTeam(updatedTeam);
                setFormData(prev => ({
                    ...prev,
                    teamLead: updatedTeam.teamLead?._id || null,
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    teamLead: userId,
                }));
            }
        } catch (error) {
            console.error('Error setting team lead:', error);
            setError(error.message);
        }
    };

    const handleAddProject = () => {
        if (!selectedProject) return;
        if (!formData.projects.includes(selectedProject)) {
            setFormData(prev => ({
                ...prev,
                projects: [...prev.projects, selectedProject],
            }));
        }
        setSelectedProject('');
    };

    const handleRemoveProject = (projectId) => {
        setFormData(prev => ({
            ...prev,
            projects: prev.projects.filter(p => p !== projectId),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Verify team lead is still valid before submitting
            const teamLeadId = formData.teamLead;
            const teamLeadStillValid = teamLeadId ? 
                availableUsers.find(u => u._id === teamLeadId && u.role === 'team_lead') : false;

            const submissionData = {
                ...formData,
                teamLead: teamLeadStillValid ? teamLeadId : null,
                projects: formData.projects || []
            };

            console.log('Submitting team data:', submissionData);
            
            if (selectedTeam) {
                // Update team and projects in a single operation
                const updatedTeam = await updateTeam(selectedTeam._id, submissionData);
                console.log('Team updated:', updatedTeam);
            } else {
                // Create team with projects
                const newTeam = await createTeam(submissionData);
                console.log('Team created:', newTeam);
            }
            
            handleFormClose();
            await fetchTeams();
            setError(null);
        } catch (error) {
            console.error('Error saving team:', error);
            setError(error.message);
            if (error.message.includes('authentication') || error.message.includes('token')) {
                window.location.href = '/login';
            }
        }
    };

    const handleDeleteClick = (team) => {
        setSelectedTeam(team);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteTeam(selectedTeam._id);
            setIsDeleteDialogOpen(false);
            setSelectedTeam(null);
            fetchTeams();
            setError(null);
        } catch (error) {
            console.error('Error deleting team:', error);
            setError(error.message);
            if (error.message.includes('authentication') || error.message.includes('token')) {
                window.location.href = '/login';
            }
        }
    };

    const handleErrorClose = () => {
        setError(null);
    };

    const formatMembersList = (members) => {
        return members?.map(member => 
            `${member.firstName} ${member.lastName}`
        ).join(', ') || 'No members';
    };

    const getMemberDetails = (userId) => {
        const user = availableUsers.find(u => u._id === userId);
        return user ? {
            name: `${user.firstName} ${user.lastName}`,
            role: user.role
        } : { name: '', role: '' };
    };

    const getProjectName = (projectId) => {
        const project = availableProjects.find(p => p._id === projectId);
        return project ? project.name : '';
    };

    return (
        <Box sx={{ 
            p: 3,
            '@media (max-width:600px)': {
                p: 1, // Much more reduced padding for mobile
                paddingTop: 0, // No top padding - layout provides spacing
                paddingBottom: '88px', // Account for bottom navigation
            },
            '@media (max-width:900px) and (orientation: landscape)': {
                p: 0.5, // Minimal padding in landscape mode
                paddingTop: 0, // No top padding
                paddingBottom: 0.5, // Minimal bottom padding
                height: 'auto', // Allow natural height
                overflow: 'visible', // Allow content to overflow
                minHeight: 'auto', // Remove height constraints
            }
        }}>
            <Snackbar 
                open={!!error} 
                autoHideDuration={6000} 
                onClose={handleErrorClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleErrorClose} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'center', sm: 'flex-end' }, 
                mb: 3 
            }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleFormOpen()}
                    sx={{
                        '@media (max-width:600px)': {
                            fontSize: '0.875rem',
                            padding: '8px 16px',
                        }
                    }}
                >
                    Add Team
                </Button>
            </Box>

            <TableContainer 
                component={Paper}
                sx={{
                    overflow: 'auto',
                    '& .MuiTable-root': {
                        borderCollapse: 'separate',
                        borderSpacing: 0
                    }
                }}
            >
                <Table 
                    size="small" 
                    stickyHeader
                    sx={{
                        '@media (min-width:901px)': {
                            '& .MuiTableCell-root': {
                                padding: '16px',
                            }
                        }
                    }}
                >
                    <TableHead sx={{ bgcolor: '#e0e0e0' }}>
                        <TableRow>
                            <TableCell 
                                onClick={() => {
                                    setSortConfig(prev => ({
                                        direction: prev.field === 'name' && prev.direction === 'asc' ? 'desc' : 'asc',
                                        field: 'name'
                                    }));
                                }}
                                sx={{ 
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                    userSelect: 'none',
                                    '@media (max-width:900px)': {
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                    }
                                }}
                            >
                                Team Name
                                {sortConfig.field === 'name' && (
                                    <span style={{ marginLeft: '4px' }}>
                                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                                    </span>
                                )}
                            </TableCell>
                            <TableCell sx={{
                                '@media (max-width:900px)': {
                                    padding: '8px',
                                    fontSize: '0.75rem',
                                }
                            }}>
                                Members
                            </TableCell>
                            <TableCell 
                                align="right"
                                sx={{
                                    '@media (max-width:900px)': {
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                    }
                                }}
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[...teams]
                            .sort((a, b) => {
                                if (sortConfig.field === 'name') {
                                    return sortConfig.direction === 'asc'
                                        ? a.name.localeCompare(b.name)
                                        : b.name.localeCompare(a.name);
                                }
                                return 0;
                            })
                            .map((team) => (
                            <TableRow key={team._id}>
                                <TableCell sx={{
                                    '@media (max-width:900px)': {
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                    }
                                }}>
                                    {team.name}
                                </TableCell>
                                <TableCell sx={{
                                    '@media (max-width:900px)': {
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                    }
                                }}>
                                    {formatMembersList(team.members)}
                                </TableCell>
                                <TableCell 
                                    align="right"
                                    sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                        }
                                    }}
                                >
                                    <IconButton
                                        onClick={() => handleFormOpen(team)}
                                        size="small"
                                        sx={{
                                            '@media (max-width:900px)': {
                                                padding: '4px',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '1rem',
                                                }
                                            }
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => handleDeleteClick(team)}
                                        size="small"
                                        sx={{
                                            '@media (max-width:900px)': {
                                                padding: '4px',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '1rem',
                                                }
                                            }
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Team Form Dialog */}
            <Dialog
                open={isFormOpen}
                onClose={handleFormClose}
                maxWidth="md"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        bgcolor: 'background.paper',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column'
                    },
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                }}
            >
                <DialogTitle sx={{
                    '@media (max-width:600px)': {
                        fontSize: '1.1rem',
                        padding: '16px',
                        paddingBottom: '8px',
                    }
                }}>
                    {selectedTeam ? 'Edit Team' : 'Create Team'}
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent sx={{ 
                        flex: 1, 
                        overflow: 'auto',
                        paddingTop: '24px !important',
                        '@media (max-width:600px)': {
                            padding: '16px',
                            paddingTop: '24px !important',
                        }
                    }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    name="name"
                                    label="Team Name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                    margin="normal"
                                    sx={{
                                        '@media (max-width:600px)': {
                                            marginBottom: '16px',
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                                height: '40px',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.875rem',
                                                top: '-2px',
                                                '&.MuiInputLabel-shrunk': {
                                                    top: '-6px',
                                                }
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px 14px',
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                                    Team Members
                                </Typography>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs>
                                        <FormControl fullWidth>
                                            <InputLabel>Add Member</InputLabel>
                                            <Select
                                                value={selectedUser}
                                                onChange={(e) => setSelectedUser(e.target.value)}
                                                label="Add Member"
                                            >
                                                {availableUsers.map((user) => (
                                                    <MenuItem 
                                                        key={user._id} 
                                                        value={user._id}
                                                        disabled={formData.members.includes(user._id)}
                                                    >
                                                        {`${user.firstName} ${user.lastName}`}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            variant="contained"
                                            onClick={handleAddMember}
                                            disabled={!selectedUser}
                                        >
                                            Add Member
                                        </Button>
                                    </Grid>
                                </Grid>

                                <List>
                                    {formData.members.map((userId, index) => (
                                        <React.Fragment key={userId}>
                                            <ListItem>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={formData.teamLead === userId}
                                                            onChange={() => handleTeamLeadChange(userId)}
                                                            disabled={getMemberDetails(userId).role !== 'team_lead'}
                                                        />
                                                    }
                                                    label={
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <PersonIcon />
                                                            <span>{getMemberDetails(userId).name}</span>
                                                            {formData.teamLead === userId && (
                                                                <Chip size="small" color="primary" label="Team Lead" />
                                                            )}
                                                        </Stack>
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleRemoveMember(userId)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            {index < formData.members.length - 1 && (
                                                <Divider component="li" />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                                    Projects
                                </Typography>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs>
                                        <FormControl fullWidth>
                                            <InputLabel>Add Project</InputLabel>
                                            <Select
                                                value={selectedProject}
                                                onChange={(e) => setSelectedProject(e.target.value)}
                                                label="Add Project"
                                            >
                                                {activeProjects.map((project) => (
                                                    <MenuItem 
                                                        key={project._id} 
                                                        value={project._id}
                                                        disabled={formData.projects.includes(project._id)}
                                                    >
                                                        {project.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            variant="contained"
                                            onClick={handleAddProject}
                                            disabled={!selectedProject}
                                        >
                                            Add Project
                                        </Button>
                                    </Grid>
                                </Grid>

                                <List>
                                    {formData.projects.map((projectId, index) => (
                                        <React.Fragment key={projectId}>
                                            <ListItem>
                                                <ListItemText primary={getProjectName(projectId)} />
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleRemoveProject(projectId)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            {index < formData.projects.length - 1 && (
                                                <Divider component="li" />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'flex-end' }}>
                        <Button onClick={handleFormClose}>Cancel</Button>
                        <Button type="submit" variant="contained" color="primary">
                            {selectedTeam ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete {selectedTeam?.name}? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Teams;
